import { AlertTriangle, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface Props {
    /** Plaintext token to display. */
    token: string;
    open: boolean;
    /**
     * Called when the user confirms they've saved the token. The parent
     * must discard the token from state after this fires.
     */
    onClose: () => void;
}

/**
 * One-shot display modal for a freshly-issued gateway bearer token.
 *
 * Hard requirements that this component enforces (matches the API
 * contract — the server never returns the token again):
 * - Token is shown in a monospace block with a copy button.
 * - The dialog cannot be dismissed via outside-click or Escape until
 *   the user explicitly acknowledges they've saved it.
 * - The parent is expected to discard `token` from state once
 *   `onClose` fires.
 */
export function TokenShownOnceModal({ token, open, onClose }: Props) {
    const [acknowledged, setAcknowledged] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(token);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error('Failed to copy token — select and copy manually.');
        }
    };

    const handleClose = () => {
        if (!acknowledged) return;
        setAcknowledged(false);
        setCopied(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                // Block close attempts until the user confirms.
                if (!next && acknowledged) handleClose();
            }}
        >
            <DialogContent
                className="sm:max-w-lg"
                onInteractOutside={(e) => {
                    if (!acknowledged) e.preventDefault();
                }}
                onEscapeKeyDown={(e) => {
                    if (!acknowledged) e.preventDefault();
                }}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Save your gateway token
                    </DialogTitle>
                    <DialogDescription>
                        This token will not be shown again. Copy it now and store it in your gateway
                        configuration — there is no way to recover it later.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div className="relative">
                        <pre className="overflow-x-auto rounded-md border bg-muted p-3 pr-12 font-mono text-sm break-all whitespace-pre-wrap">
                            {token}
                        </pre>
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="absolute right-1 top-1 h-8 w-8"
                            onClick={handleCopy}
                            aria-label="Copy token"
                        >
                            {copied ? (
                                <Check className="h-4 w-4 text-green-600" />
                            ) : (
                                <Copy className="h-4 w-4" />
                            )}
                        </Button>
                    </div>

                    <div className="flex items-start gap-3">
                        <Checkbox
                            id="ack"
                            checked={acknowledged}
                            onCheckedChange={(v) => setAcknowledged(v === true)}
                        />
                        <Label htmlFor="ack" className="text-sm font-normal">
                            I have saved this token. I understand it will not be shown again.
                        </Label>
                    </div>
                </div>

                <DialogFooter>
                    <Button onClick={handleClose} disabled={!acknowledged}>
                        Done
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
